package com.arenaproplay.admin.pages.clubs

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.*
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.components.forms.AddressAutocompleteField
import com.arenaproplay.admin.ui.components.forms.AmenityChips
import com.arenaproplay.admin.ui.components.forms.CoordinatesHint
import com.arenaproplay.admin.ui.components.forms.DayHours
import com.arenaproplay.admin.ui.components.forms.FormButton
import com.arenaproplay.admin.ui.components.forms.OTHER_SPORT
import com.arenaproplay.admin.ui.components.forms.SportTypeSelect
import com.arenaproplay.admin.ui.components.forms.TextArea
import com.arenaproplay.admin.ui.components.forms.TextInput
import com.arenaproplay.admin.ui.components.forms.WorkingHoursEditor
import com.arenaproplay.admin.ui.components.forms.defaultWeekHours
import com.arenaproplay.admin.ui.components.forms.parseRatingInput
import com.arenaproplay.admin.ui.components.icons.LucideIcon
import com.arenaproplay.admin.ui.components.overlay.ConfirmActionOverlay
import com.arenaproplay.admin.navigation.Routes
import com.arenaproplay.admin.ui.layouts.AdminLayout
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.displaySmall
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.utils.pickImageFile
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.sPadding
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Img

@Page("/clubs/{id}")
@Composable
fun ClubDetailsPage() {
    val repo = LocalAppContainer.current.adminRepository ?: return
    val ctx = rememberPageContext()
    val clubId = ctx.route.params["id"] ?: ctx.route.path.substringAfterLast("/")
    val loadState = rememberLoadState()

    var club by remember { mutableStateOf<ClubRow?>(null) }
    var gallery by remember { mutableStateOf<List<ClubGalleryImage>>(emptyList()) }
    var rules by remember { mutableStateOf<List<ClubRule>>(emptyList()) }
    var services by remember { mutableStateOf<List<ClubService>>(emptyList()) }
    var amenities by remember { mutableStateOf<List<String>>(emptyList()) }
    var sports by remember { mutableStateOf<List<SportOption>>(emptyList()) }
    var week by remember { mutableStateOf(defaultWeekHours()) }

    val scope = rememberCoroutineScope()

    // Delete confirmation via ConfirmActionOverlay (Phase-2 pattern)
    var showDelete by remember { mutableStateOf(false) }
    var deleting by remember { mutableStateOf(false) }
    var deleteError by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(clubId, loadState.reloadToken) {
        club = null
        loadState.load {
            val loaded = repo.getClub(clubId)
            club = loaded
            gallery = loaded.gallery.orEmpty()
            rules = loaded.rules.orEmpty()
            services = repo.getClubServices(clubId)
            amenities = repo.getClubAmenities(clubId)
            runCatching {
                val saved = repo.getClubSchedule(clubId).workingHours
                week = defaultWeekHours().map { day ->
                    saved.find { it.dayOfWeek == day.dayOfWeek }
                        ?.let { DayHours(it.dayOfWeek, it.openTime.take(5), it.closeTime.take(5), it.isClosed) }
                        ?: day
                }
            }
        }
    }
    LaunchedEffect(Unit) {
        runCatching { sports = repo.getSports() }
    }

    AdminLayout(
        title = club?.name ?: "Клуб",
        subtitle = "Редактирование клуба: фото, услуги, расписание, правила",
        actions = {
            if (club != null) {
                DeleteClubHeaderButton { showDelete = true; deleteError = null }
            }
        },
    ) {
        if (loadState.error != null) {
            ErrorCard(loadState)
            return@AdminLayout
        }

        val loaded = club
        if (loaded == null) {
            // Skeleton state while loading
            ClubDetailSkeleton()
            return@AdminLayout
        }

        // ── Club info card ────────────────────────────────────────────────────
        ClubInfoCard(
            club = loaded,
            sports = sports,
            updateClub = { repo.updateClub(clubId, it) },
            onUpdated = { club = it },
            autocomplete = { q, s -> repo.autocompleteAddress(q, s) },
            resolvePlace = { p, s -> repo.getPlaceAddress(p, s) },
        )
        VSpace(16)

        // ── Schedule card ─────────────────────────────────────────────────────
        ClubScheduleCard(
            week = week,
            onChange = { week = it },
            save = { entries -> repo.updateClubWorkingHours(clubId, entries) },
        )
        VSpace(16)

        // ── Photos card ───────────────────────────────────────────────────────
        ClubPhotosCard(
            club = loaded,
            gallery = gallery,
            uploadImage = { b, n, t -> repo.uploadClubImage(b, n, t) },
            updateClub = { repo.updateClub(clubId, it) },
            updateGallery = { repo.updateClubGallery(clubId, it) },
            onClubUpdated = { club = it },
            onGalleryUpdated = { gallery = it },
        )
        VSpace(16)

        // ── Services card ─────────────────────────────────────────────────────
        ClubServicesCard(
            services = services,
            createService = { repo.createClubService(clubId, it) },
            updateService = { id, req -> repo.updateClubService(clubId, id, req) },
            deleteService = { repo.deleteClubService(clubId, it) },
            onChanged = { services = it },
        )
        VSpace(16)

        // ── Rules card ────────────────────────────────────────────────────────
        ClubRulesCard(
            rules = rules,
            updateRules = { repo.updateClubRules(clubId, it) },
            onChanged = { rules = it },
        )
        VSpace(16)

        // ── Amenities card ────────────────────────────────────────────────────
        ClubAmenitiesCard(
            amenities = amenities,
            updateAmenities = { repo.updateClubAmenities(clubId, it) },
            onChanged = { amenities = it },
        )
        VSpace(28)
    }

    // ── Delete confirmation overlay (Phase-2 pattern — ConfirmActionOverlay) ─
    val loaded2 = club
    if (showDelete && loaded2 != null) {
        ConfirmActionOverlay(
            isOpen = true,
            onClose = { if (!deleting) { showDelete = false; deleteError = null } },
            title = "Удалить клуб",
            description = "Это безвозвратно удалит «${loaded2.name}» вместе с полями, " +
                "расписанием, галереей, услугами и отзывами. Действие нельзя отменить.",
            warning = null,
            error = deleteError,
            confirmLabel = if (deleting) "Удаляем…" else "Удалить клуб",
            confirmDanger = true,
            confirmEnabled = !deleting,
        ) {
            deleting = true
            deleteError = null
            scope.launch {
                try {
                    repo.deleteClub(clubId)
                    showDelete = false
                    ctx.router.navigateTo(Routes.CLUBS)
                } catch (e: Throwable) {
                    deleteError = e.message ?: "Не удалось удалить клуб"
                } finally {
                    deleting = false
                }
            }
        }
    }
}

// ── Skeleton state ────────────────────────────────────────────────────────────

@Composable
private fun ClubDetailSkeleton() {
    repeat(3) { i ->
        SectionCard(modifier = Modifier.fillMaxWidth()) {
            repeat(4) { j ->
                Box(
                    Modifier.fillMaxWidth().height(14.px).sBorderRadius(6)
                        .margin(bottom = if (j < 3) 12.px else 0.px)
                        .styleModifier {
                            property("width", "${listOf(80, 60, 90, 50)[j % 4]}%")
                            property("background", AppColors.Background.toRgbString())
                            property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                        }
                )
            }
        }
        if (i < 2) VSpace(16)
    }
}

// ── Danger header button ──────────────────────────────────────────────────────

@Composable
private fun DeleteClubHeaderButton(onClick: () -> Unit) {
    Box(
        Modifier
            .sBorderRadius(999)
            .padding(left = 16.px, right = 20.px, top = 9.px, bottom = 9.px)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("background", AppColors.ErrorTint.toRgbString())
                property("border", "1px solid ${AppColors.Error.toRgbaString(0.20f)}")
                property("transition", "opacity 140ms ease")
            }
            .onClick { onClick() },
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            LucideIcon(name = "trash-2", size = 15, color = AppColors.Error.toRgbString())
            HSpace(8)
            SpanText(
                "Удалить клуб",
                Modifier.bodySMedium().color(AppColors.Error)
                    .styleModifier { property("font-weight", "600") },
            )
        }
    }
}

// ── Section card title ────────────────────────────────────────────────────────

@Composable
private fun CardSectionTitle(text: String) {
    SpanText(
        text,
        Modifier.displaySmall().color(AppColors.Ink).margin(bottom = 16.px),
    )
}

// ── Inline error ──────────────────────────────────────────────────────────────

@Composable
private fun InlineError(message: String?) {
    if (message != null) {
        Box(Modifier.height(10.px))
        SpanText(message, Modifier.bodySMedium().color(AppColors.Error))
    }
}

// ── Saved banner ──────────────────────────────────────────────────────────────

@Composable
private fun SavedBadge(saved: Boolean) {
    if (saved) {
        SpanText("Сохранено", Modifier.bodySMedium().color(AppColors.Success))
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Info card
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun ClubInfoCard(
    club: ClubRow,
    sports: List<SportOption>,
    updateClub: suspend (UpdateClubRequest) -> ClubRow,
    onUpdated: (ClubRow) -> Unit,
    autocomplete: suspend (String, String?) -> List<AddressSuggestion>,
    resolvePlace: suspend (String, String?) -> PlaceAddress,
) {
    val scope = rememberCoroutineScope()
    var name by remember(club.id) { mutableStateOf(club.name) }
    var address by remember(club.id) { mutableStateOf(club.address) }
    var latitude by remember(club.id) { mutableStateOf(club.latitude) }
    var longitude by remember(club.id) { mutableStateOf(club.longitude) }
    var phone by remember(club.id) { mutableStateOf(club.phone ?: "") }
    var sportType by remember(club.id) { mutableStateOf(club.type.uppercase()) }
    var customSportName by remember(club.id) { mutableStateOf(club.customSportName ?: "") }
    var description by remember(club.id) { mutableStateOf(club.description ?: "") }
    var ratingText by remember(club.id) { mutableStateOf(club.averageRating.toString()) }
    var reviewsText by remember(club.id) { mutableStateOf(club.totalRatingsCount.toString()) }
    var instagram by remember(club.id) { mutableStateOf(club.instagramUrl ?: "") }
    var website by remember(club.id) { mutableStateOf(club.websiteUrl ?: "") }
    var googleUrl by remember(club.id) { mutableStateOf(club.googleMapsUrl ?: "") }
    var googleSearch by remember(club.id) { mutableStateOf(club.googleSearchUrl ?: "") }
    var saving by remember { mutableStateOf(false) }
    var saved by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    SectionCard(
        title = "Основная информация",
        modifier = Modifier.fillMaxWidth(),
    ) {
        Row(Modifier.fillMaxWidth()) {
            Box(Modifier.weight(1f)) {
                TextInput(name, { name = it; saved = false }, label = "Название", placeholder = "Название клуба")
            }
            HSpace(16)
            Box(Modifier.weight(1f)) {
                TextInput(phone, { phone = it; saved = false }, label = "Телефон", placeholder = "+380…")
            }
        }
        Box(Modifier.height(14.px))
        AddressAutocompleteField(
            value = address,
            onValueChange = { address = it; saved = false },
            onResolved = {
                address = it.address
                latitude = it.latitude
                longitude = it.longitude
                saved = false
            },
            autocomplete = autocomplete,
            resolvePlace = resolvePlace,
            latitude = latitude,
            longitude = longitude,
        )
        CoordinatesHint(latitude, longitude)
        Box(Modifier.height(14.px))
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.Bottom) {
            Box(Modifier.weight(1f)) {
                SportTypeSelect(sports, sportType, label = "Вид спорта") {
                    sportType = it
                    saved = false
                    if (it != OTHER_SPORT) customSportName = ""
                }
            }
            if (sportType == OTHER_SPORT) {
                HSpace(16)
                Box(Modifier.weight(1f)) {
                    TextInput(
                        customSportName, { customSportName = it; saved = false },
                        label = "Название вида спорта", placeholder = "напр. Падел",
                    )
                }
            }
        }
        Box(Modifier.height(14.px))
        Row(Modifier.fillMaxWidth()) {
            Box(Modifier.weight(1f)) {
                TextInput(
                    ratingText, { ratingText = it; saved = false },
                    label = "Рейтинг", placeholder = "напр. 4.5",
                )
            }
            HSpace(16)
            Box(Modifier.weight(1f)) {
                TextInput(
                    reviewsText, { reviewsText = it; saved = false },
                    label = "Количество отзывов", placeholder = "напр. 128",
                )
            }
        }
        Box(Modifier.height(14.px))
        Row(Modifier.fillMaxWidth()) {
            Box(Modifier.weight(1f)) {
                TextInput(
                    instagram, { instagram = it; saved = false },
                    label = "Instagram", placeholder = "https://instagram.com/…",
                )
            }
            HSpace(16)
            Box(Modifier.weight(1f)) {
                TextInput(
                    website, { website = it; saved = false },
                    label = "Веб-сайт", placeholder = "https://…",
                )
            }
            HSpace(16)
            Box(Modifier.weight(1f)) {
                TextInput(
                    googleSearch, { googleSearch = it; saved = false },
                    label = "Google Поиск", placeholder = "https://www.google.com/search?q=…",
                )
            }
            HSpace(16)
            Box(Modifier.weight(1f)) {
                TextInput(
                    googleUrl, { googleUrl = it; saved = false },
                    label = "Google Карты", placeholder = "https://maps.google.com/…",
                )
            }
        }
        Box(Modifier.height(14.px))
        TextArea(
            description, { description = it; saved = false },
            label = "Описание", placeholder = "Краткое описание (необязательно)", rows = 3,
        )
        InlineError(error)
        Box(Modifier.height(18.px))
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            SavedBadge(saved)
            Box(Modifier.weight(1f))
            FormButton(if (saving) "Сохранение…" else "Сохранить изменения", enabled = !saving, primary = true) {
                if (name.isBlank()) {
                    error = "Название клуба обязательно"
                    return@FormButton
                }
                if (sportType == OTHER_SPORT && customSportName.isBlank()) {
                    error = "Для типа «Другой» укажите название вида спорта"
                    return@FormButton
                }
                val rating = ratingText.trim().takeIf { it.isNotBlank() }?.let {
                    val r = parseRatingInput(it)
                    if (r == null || r < 0.0 || r > 5.0) {
                        error = "Рейтинг — число от 0 до 5, напр. 4.5"
                        return@FormButton
                    }
                    r
                }
                val reviews = reviewsText.trim().takeIf { it.isNotBlank() }?.let {
                    val n = it.toIntOrNull()
                    if (n == null || n < 0) {
                        error = "Количество отзывов — целое неотрицательное число"
                        return@FormButton
                    }
                    n
                }
                saving = true
                error = null
                saved = false
                scope.launch {
                    try {
                        val updated = updateClub(
                            UpdateClubRequest(
                                name = name.trim(),
                                address = address.trim(),
                                latitude = latitude,
                                longitude = longitude,
                                type = sportType.ifBlank { null },
                                customSportName = customSportName.trim()
                                    .takeIf { sportType == OTHER_SPORT && it.isNotBlank() },
                                phone = phone.trim().ifBlank { null },
                                description = description.trim().ifBlank { null },
                                instagramUrl = instagram.trim(),
                                websiteUrl = website.trim(),
                                googleSearchUrl = googleSearch.trim(),
                                googleMapsUrl = googleUrl.trim(),
                                averageRating = rating?.takeIf { it != club.averageRating },
                                totalRatingsCount = reviews?.takeIf { it != club.totalRatingsCount },
                            )
                        )
                        onUpdated(updated)
                        saved = true
                    } catch (e: Throwable) {
                        error = e.message ?: "Не удалось сохранить"
                    } finally {
                        saving = false
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Schedule (weekly working hours)
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun ClubScheduleCard(
    week: List<DayHours>,
    onChange: (List<DayHours>) -> Unit,
    save: suspend (List<WorkingHoursEntry>) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var saving by remember { mutableStateOf(false) }
    var saved by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    SectionCard(
        title = "Часы работы",
        modifier = Modifier.fillMaxWidth(),
    ) {
        WorkingHoursEditor(week) { onChange(it); saved = false }
        InlineError(error)
        Box(Modifier.height(14.px))
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            SavedBadge(saved)
            Box(Modifier.weight(1f))
            FormButton(if (saving) "Сохранение…" else "Сохранить расписание", enabled = !saving, primary = true) {
                saving = true
                error = null
                scope.launch {
                    try {
                        save(week.map { WorkingHoursEntry(it.dayOfWeek, it.open, it.close, it.closed) })
                        saved = true
                    } catch (e: Throwable) {
                        error = e.message ?: "Не удалось сохранить расписание"
                    } finally {
                        saving = false
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Photos (banner + gallery)
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun ClubPhotosCard(
    club: ClubRow,
    gallery: List<ClubGalleryImage>,
    uploadImage: suspend (ByteArray, String, String) -> String,
    updateClub: suspend (UpdateClubRequest) -> ClubRow,
    updateGallery: suspend (List<String>) -> List<ClubGalleryImage>,
    onClubUpdated: (ClubRow) -> Unit,
    onGalleryUpdated: (List<ClubGalleryImage>) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var uploadingBanner by remember { mutableStateOf(false) }
    var uploadingGallery by remember { mutableStateOf(false) }
    var urlInput by remember { mutableStateOf("") }
    var addingUrl by remember { mutableStateOf(false) }
    var bannerUrlInput by remember { mutableStateOf("") }
    var settingBannerUrl by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    SectionCard(
        title = "Фотографии",
        modifier = Modifier.fillMaxWidth(),
    ) {
        // Main photo label
        SpanText(
            "Главное фото",
            Modifier.bodySMedium().color(AppColors.Muted).margin(bottom = 8.px),
        )
        Row(verticalAlignment = Alignment.Bottom) {
            Box(
                Modifier.width(320.px).height(180.px).sBorderRadius(12)
                    .overflow(Overflow.Hidden)
                    .styleModifier { property("background", AppColors.Background.toRgbString()) },
                contentAlignment = Alignment.Center,
            ) {
                val url = club.mainPhotoUrl
                if (url != null) {
                    Img(
                        src = url,
                        attrs = Modifier.fillMaxSize()
                            .styleModifier { property("object-fit", "cover") }
                            .toAttrs(),
                    )
                } else {
                    LucideIcon(name = "image", size = 34, color = AppColors.Faint.toRgbString())
                }
            }
            HSpace(16)
            FormButton(
                if (uploadingBanner) "Загрузка…" else "Изменить фото",
                enabled = !uploadingBanner,
                primary = false,
            ) {
                pickImageFile { bytes, fileName, contentType ->
                    uploadingBanner = true
                    error = null
                    scope.launch {
                        try {
                            val imageUrl = uploadImage(bytes, fileName, contentType)
                            onClubUpdated(updateClub(UpdateClubRequest(mainPhotoUrl = imageUrl)))
                        } catch (e: Throwable) {
                            error = e.message ?: "Не удалось загрузить фото"
                        } finally {
                            uploadingBanner = false
                        }
                    }
                }
            }
        }

        Box(Modifier.height(12.px))
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Box(Modifier.weight(1f)) {
                TextInput(
                    bannerUrlInput, { bannerUrlInput = it },
                    placeholder = "Или вставьте ссылку на главное фото (https://…)",
                )
            }
            HSpace(12)
            FormButton(
                if (settingBannerUrl) "Устанавливаем…" else "По ссылке",
                enabled = !settingBannerUrl,
                primary = false,
            ) {
                val url = bannerUrlInput.trim()
                if (!url.startsWith("http://") && !url.startsWith("https://")) {
                    error = "Вставьте прямую ссылку на изображение, начинающуюся с http(s)://"
                    return@FormButton
                }
                settingBannerUrl = true
                error = null
                scope.launch {
                    try {
                        onClubUpdated(updateClub(UpdateClubRequest(mainPhotoUrl = url)))
                        bannerUrlInput = ""
                    } catch (e: Throwable) {
                        error = e.message ?: "Не удалось установить главное фото"
                    } finally {
                        settingBannerUrl = false
                    }
                }
            }
        }

        Box(Modifier.height(22.px))
        SpanText(
            "Галерея",
            Modifier.bodySMedium().color(AppColors.Muted).margin(bottom = 8.px),
        )
        Row(
            Modifier.fillMaxWidth().styleModifier {
                property("flex-wrap", "wrap")
                property("gap", "12px")
            }
        ) {
            gallery.sortedBy { it.sortOrder }.forEach { image ->
                Box(
                    Modifier.width(150.px).height(100.px).sBorderRadius(10)
                        .overflow(Overflow.Hidden)
                        .styleModifier {
                            property("position", "relative")
                            property("background", AppColors.Background.toRgbString())
                        },
                ) {
                    Img(
                        src = image.imageUrl,
                        attrs = Modifier.fillMaxSize()
                            .styleModifier { property("object-fit", "cover") }
                            .toAttrs(),
                    )
                    Box(
                        Modifier
                            .styleModifier {
                                property("position", "absolute")
                                property("top", "6px")
                                property("right", "6px")
                                property("background", AppColors.Ink.toRgbaString(0.65f))
                            }
                            .sBorderRadius(999)
                            .padding(5.px)
                            .cursor(Cursor.Pointer)
                            .onClick {
                                error = null
                                scope.launch {
                                    try {
                                        val remaining = gallery.sortedBy { it.sortOrder }
                                            .filter { it.id != image.id }
                                            .map { it.imageUrl }
                                        onGalleryUpdated(updateGallery(remaining))
                                    } catch (e: Throwable) {
                                        error = e.message ?: "Не удалось удалить фото"
                                    }
                                }
                            },
                        contentAlignment = Alignment.Center,
                    ) {
                        LucideIcon(name = "x", size = 12, color = AppColors.Surface.toRgbString())
                    }
                }
            }
            // Upload tile
            Box(
                Modifier.width(150.px).height(100.px).sBorderRadius(10)
                    .cursor(if (uploadingGallery) Cursor.NotAllowed else Cursor.Pointer)
                    .styleModifier {
                        property("border", "1.5px dashed ${AppColors.Ink.toRgbaString(0.18f)}")
                        property("background", AppColors.Background.toRgbString())
                    }
                    .onClick {
                        if (uploadingGallery) return@onClick
                        pickImageFile { bytes, fileName, contentType ->
                            uploadingGallery = true
                            error = null
                            scope.launch {
                                try {
                                    val imageUrl = uploadImage(bytes, fileName, contentType)
                                    val urls = gallery.sortedBy { it.sortOrder }
                                        .map { it.imageUrl } + imageUrl
                                    onGalleryUpdated(updateGallery(urls))
                                } catch (e: Throwable) {
                                    error = e.message ?: "Не удалось загрузить фото"
                                } finally {
                                    uploadingGallery = false
                                }
                            }
                        }
                    },
                contentAlignment = Alignment.Center,
            ) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    LucideIcon(name = "plus", size = 18, color = AppColors.Muted.toRgbString())
                    Box(Modifier.height(4.px))
                    SpanText(
                        if (uploadingGallery) "Загрузка…" else "Добавить фото",
                        Modifier.bodySMedium().color(AppColors.Muted),
                    )
                }
            }
        }

        Box(Modifier.height(14.px))
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Box(Modifier.weight(1f)) {
                TextInput(
                    urlInput, { urlInput = it },
                    placeholder = "Или вставьте ссылку на фото (https://…)",
                )
            }
            HSpace(12)
            FormButton(
                if (addingUrl) "Добавляем…" else "По ссылке",
                enabled = !addingUrl,
                primary = false,
            ) {
                val url = urlInput.trim()
                if (!url.startsWith("http://") && !url.startsWith("https://")) {
                    error = "Вставьте прямую ссылку на изображение, начинающуюся с http(s)://"
                    return@FormButton
                }
                addingUrl = true
                error = null
                scope.launch {
                    try {
                        val urls = gallery.sortedBy { it.sortOrder }.map { it.imageUrl } + url
                        onGalleryUpdated(updateGallery(urls))
                        urlInput = ""
                    } catch (e: Throwable) {
                        error = e.message ?: "Не удалось добавить фото"
                    } finally {
                        addingUrl = false
                    }
                }
            }
        }
        InlineError(error)
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Services
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun ClubServicesCard(
    services: List<ClubService>,
    createService: suspend (CreateClubServiceRequest) -> ClubService,
    updateService: suspend (String, UpdateClubServiceRequest) -> ClubService,
    deleteService: suspend (String) -> Unit,
    onChanged: (List<ClubService>) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var newName by remember { mutableStateOf("") }
    var newPrice by remember { mutableStateOf("") }
    var newDescription by remember { mutableStateOf("") }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    SectionCard(
        title = "Услуги",
        modifier = Modifier.fillMaxWidth(),
    ) {
        if (services.isEmpty()) {
            SpanText("Услуги не добавлены", Modifier.bodyMRegular().color(AppColors.Faint))
        }
        services.forEach { service ->
            Row(
                Modifier.fillMaxWidth().padding(topBottom = 10.px)
                    .styleModifier {
                        property("border-bottom", "1px solid ${AppColors.Border.toRgbString()}")
                    },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(Modifier.weight(1f)) {
                    SpanText(service.name, Modifier.bodyMMedium().color(AppColors.Ink))
                    if (!service.description.isNullOrBlank()) {
                        Box(Modifier.height(2.px))
                        SpanText(service.description, Modifier.bodySMedium().color(AppColors.Muted))
                    }
                }
                SpanText(service.price, Modifier.bodyMRegular().color(AppColors.Muted))
                HSpace(14)
                // Toggle status with Pill (Phase-2)
                Box(
                    Modifier.cursor(Cursor.Pointer).onClick {
                        error = null
                        scope.launch {
                            try {
                                val updated = updateService(
                                    service.id,
                                    UpdateClubServiceRequest(isEnabled = !service.isEnabled),
                                )
                                onChanged(services.map { if (it.id == service.id) updated else it })
                            } catch (e: Throwable) {
                                error = e.message ?: "Не удалось обновить услугу"
                            }
                        }
                    }
                ) {
                    if (service.isEnabled) Pill("Активна", PillTone.Success)
                    else Pill("Отключена", PillTone.Neutral)
                }
                HSpace(12)
                Box(
                    Modifier.cursor(Cursor.Pointer).padding(4.px).onClick {
                        error = null
                        scope.launch {
                            try {
                                deleteService(service.id)
                                onChanged(services.filter { it.id != service.id })
                            } catch (e: Throwable) {
                                error = e.message ?: "Не удалось удалить услугу"
                            }
                        }
                    }
                ) {
                    LucideIcon(name = "trash-2", size = 15, color = AppColors.Error.toRgbString())
                }
            }
        }

        Box(Modifier.height(18.px))
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.Bottom) {
            Box(Modifier.weight(1.4f)) {
                TextInput(newName, { newName = it }, label = "Новая услуга", placeholder = "Название")
            }
            HSpace(12)
            Box(Modifier.weight(1.8f)) {
                TextInput(newDescription, { newDescription = it }, placeholder = "Описание (необязательно)")
            }
            HSpace(12)
            Box(Modifier.weight(0.8f)) {
                TextInput(newPrice, { newPrice = it }, placeholder = "Цена")
            }
            HSpace(12)
            FormButton(if (busy) "Добавляем…" else "Добавить", enabled = !busy, primary = true) {
                if (newName.isBlank() || newPrice.isBlank()) {
                    error = "Укажите название и цену услуги"
                    return@FormButton
                }
                busy = true
                error = null
                scope.launch {
                    try {
                        val created = createService(
                            CreateClubServiceRequest(
                                name = newName.trim(),
                                description = newDescription.trim().ifBlank { null },
                                price = newPrice.trim(),
                            )
                        )
                        onChanged(services + created)
                        newName = ""
                        newPrice = ""
                        newDescription = ""
                    } catch (e: Throwable) {
                        error = e.message ?: "Не удалось добавить услугу"
                    } finally {
                        busy = false
                    }
                }
            }
        }
        InlineError(error)
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Rules
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun ClubRulesCard(
    rules: List<ClubRule>,
    updateRules: suspend (List<String>) -> List<ClubRule>,
    onChanged: (List<ClubRule>) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var newRule by remember { mutableStateOf("") }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    SectionCard(
        title = "Правила",
        modifier = Modifier.fillMaxWidth(),
    ) {
        if (rules.isEmpty()) {
            SpanText("Правила не добавлены", Modifier.bodyMRegular().color(AppColors.Faint))
        }
        rules.sortedBy { it.sortOrder }.forEachIndexed { index, rule ->
            Row(
                Modifier.fillMaxWidth().padding(topBottom = 8.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText("${index + 1}.", Modifier.bodyMRegular().color(AppColors.Faint))
                HSpace(10)
                Box(Modifier.weight(1f)) {
                    SpanText(rule.text, Modifier.bodyMRegular().color(AppColors.Muted))
                }
                Box(
                    Modifier.cursor(Cursor.Pointer).padding(4.px).onClick {
                        error = null
                        scope.launch {
                            try {
                                val remaining = rules.sortedBy { it.sortOrder }
                                    .filter { it.id != rule.id }
                                    .map { it.text }
                                onChanged(updateRules(remaining))
                            } catch (e: Throwable) {
                                error = e.message ?: "Не удалось удалить правило"
                            }
                        }
                    }
                ) {
                    LucideIcon(name = "trash-2", size = 15, color = AppColors.Error.toRgbString())
                }
            }
        }

        Box(Modifier.height(14.px))
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Box(Modifier.weight(1f)) {
                TextInput(newRule, { newRule = it }, placeholder = "Текст нового правила")
            }
            HSpace(12)
            FormButton(if (busy) "Добавляем…" else "Добавить", enabled = !busy, primary = true) {
                if (newRule.isBlank()) return@FormButton
                busy = true
                error = null
                scope.launch {
                    try {
                        val texts = rules.sortedBy { it.sortOrder }.map { it.text } + newRule.trim()
                        onChanged(updateRules(texts))
                        newRule = ""
                    } catch (e: Throwable) {
                        error = e.message ?: "Не удалось добавить правило"
                    } finally {
                        busy = false
                    }
                }
            }
        }
        InlineError(error)
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Amenities
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun ClubAmenitiesCard(
    amenities: List<String>,
    updateAmenities: suspend (List<String>) -> List<String>,
    onChanged: (List<String>) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    SectionCard(
        title = "Удобства",
        modifier = Modifier.fillMaxWidth(),
    ) {
        AmenityChips(selected = amenities.toSet(), enabled = !busy) { type ->
            busy = true
            error = null
            val next = if (type in amenities) amenities - type else amenities + type
            scope.launch {
                try {
                    onChanged(updateAmenities(next))
                } catch (e: Throwable) {
                    error = e.message ?: "Не удалось обновить удобства"
                } finally {
                    busy = false
                }
            }
        }
        InlineError(error)
    }
}
